import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { LogOut, Package, MailOpen, RefreshCw } from "lucide-react";
import { API } from "../data/content";

const ORDER_STATUSES = ["received", "confirmed", "preparing", "delivered", "cancelled"];
const ENQUIRY_STATUSES = ["new", "quoted", "booked", "closed"];

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

function StatusPill({ value, options, onChange, testId }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
            className="bg-[#1A1A1A] border border-[#D4AF37]/40 text-[#D4AF37] text-[10px] uppercase tracking-[0.15em] rounded-full px-3 py-1.5 cursor-pointer [color-scheme:dark]">
      {options.map((s) => <option key={s} value={s}>{s}</option>)}
    </select>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("novaphi_admin_token"));
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);

  const authHeader = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [o, e] = await Promise.all([
        axios.get(`${API}/admin/orders`, authHeader()),
        axios.get(`${API}/admin/enquiries`, authHeader()),
      ]);
      setOrders(o.data);
      setEnquiries(e.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("novaphi_admin_token");
        setToken(null);
      }
    } finally {
      setLoading(false);
    }
  }, [token, authHeader]);

  useEffect(() => { load(); }, [load]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoggingIn(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, form);
      localStorage.setItem("novaphi_admin_token", data.token);
      setToken(data.token);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Check your details.");
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("novaphi_admin_token");
    setToken(null);
  };

  const setOrderStatus = async (id, status) => {
    await axios.patch(`${API}/admin/orders/${id}`, { status }, authHeader());
    setOrders((prev) => prev.map((o) => o.id === id ? { ...o, status } : o));
  };

  const setEnquiryStatus = async (id, status) => {
    await axios.patch(`${API}/admin/enquiries/${id}`, { status }, authHeader());
    setEnquiries((prev) => prev.map((e) => e.id === id ? { ...e, status } : e));
  };

  const field = "w-full bg-[#1A1A1A] border border-[#F9F9F6]/15 focus:border-[#D4AF37] px-4 py-3.5 text-sm text-[#F9F9F6] placeholder:text-[#F9F9F6]/30 transition-colors";

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6" data-testid="admin-login-page">
        <form onSubmit={login} className="w-full max-w-sm border border-[#D4AF37]/30 bg-[#0A0A0A] p-10">
          <img src="/assets/logo-white.png" alt="Novaphi Catering" className="h-16 w-auto mb-8" />
          <h1 className="font-serif-e text-3xl text-[#F9F9F6]">Admin Login</h1>
          <p className="text-[#F9F9F6]/50 text-xs mt-2 mb-8">Orders &amp; enquiries dashboard</p>
          <div className="space-y-3">
            <input type="email" required placeholder="Email" value={form.email} data-testid="admin-email-input"
                   onChange={(e) => setForm({ ...form, email: e.target.value })} className={field} />
            <input type="password" required placeholder="Password" value={form.password} data-testid="admin-password-input"
                   onChange={(e) => setForm({ ...form, password: e.target.value })} className={field} />
          </div>
          {error && <p data-testid="admin-login-error" className="text-red-400 text-xs mt-4">{error}</p>}
          <button type="submit" disabled={loggingIn} data-testid="admin-login-btn"
                  className="w-full mt-6 rounded-full bg-[#D4AF37] text-[#0A0A0A] py-3.5 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#F9F9F6] transition-colors disabled:opacity-50">
            {loggingIn ? "Signing in…" : "Sign In"}
          </button>
          <a href="/" data-testid="admin-back-home" className="block text-center text-[10px] uppercase tracking-[0.2em] text-[#F9F9F6]/40 hover:text-[#D4AF37] mt-6 transition-colors">
            ← Back to site
          </a>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F9F9F6]" data-testid="admin-dashboard">
      <header className="border-b border-[#F9F9F6]/10 bg-[#0A0A0A] sticky top-0 z-40">
        <div className="max-w-[1200px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/assets/logo-white.png" alt="Novaphi Catering" className="h-10 w-auto" />
            <span className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] border border-[#D4AF37]/40 rounded-full px-3 py-1">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} data-testid="admin-refresh-btn" aria-label="Refresh"
                    className="w-9 h-9 rounded-full border border-[#F9F9F6]/20 flex items-center justify-center text-[#F9F9F6]/60 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
              <RefreshCw size={14} strokeWidth={1.5} className={loading ? "animate-spin" : ""} />
            </button>
            <button onClick={logout} data-testid="admin-logout-btn"
                    className="flex items-center gap-2 rounded-full border border-[#F9F9F6]/20 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-[#F9F9F6]/60 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
              <LogOut size={13} strokeWidth={1.5} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 py-10">
        <div className="flex gap-3 mb-8" data-testid="admin-tabs">
          <button onClick={() => setTab("orders")} data-testid="tab-orders"
                  className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-[11px] uppercase tracking-[0.15em] border transition-colors ${tab === "orders" ? "bg-[#D4AF37] text-[#0A0A0A] border-[#D4AF37] font-semibold" : "border-[#F9F9F6]/20 text-[#F9F9F6]/60"}`}>
            <Package size={14} strokeWidth={1.5} /> Orders ({orders.length})
          </button>
          <button onClick={() => setTab("enquiries")} data-testid="tab-enquiries"
                  className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-[11px] uppercase tracking-[0.15em] border transition-colors ${tab === "enquiries" ? "bg-[#D4AF37] text-[#0A0A0A] border-[#D4AF37] font-semibold" : "border-[#F9F9F6]/20 text-[#F9F9F6]/60"}`}>
            <MailOpen size={14} strokeWidth={1.5} /> Enquiries ({enquiries.length})
          </button>
        </div>

        {tab === "orders" && (
          <div className="space-y-4" data-testid="orders-list">
            {orders.length === 0 && <p className="text-[#F9F9F6]/40 text-sm">No orders yet.</p>}
            {orders.map((o) => (
              <article key={o.id} data-testid={`order-row-${o.ref}`} className="border border-[#F9F9F6]/10 bg-[#1A1A1A] p-5 md:p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="font-serif-e text-xl text-[#D4AF37]">{o.ref}</p>
                    <p className="text-sm text-[#F9F9F6] mt-1">{o.customer_name} · <a href={`tel:${o.phone}`} className="text-[#D4AF37] hover:underline">{o.phone}</a>{o.email && <> · <a href={`mailto:${o.email}`} className="text-[#D4AF37] hover:underline">{o.email}</a></>}</p>
                    {o.delivery && <p className="text-[11px] text-[#F9F9F6]/60 mt-1">Delivery: {o.delivery}</p>}
                    <p className="text-[10px] uppercase tracking-[0.15em] text-[#F9F9F6]/40 mt-1">{fmtDate(o.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <p className="font-serif-e text-2xl text-[#F9F9F6]" data-testid={`order-total-${o.ref}`}>R{o.total.toLocaleString()}</p>
                    <StatusPill value={o.status} options={ORDER_STATUSES} onChange={(s) => setOrderStatus(o.id, s)} testId={`order-status-${o.ref}`} />
                  </div>
                </div>
                <ul className="mt-4 space-y-1 border-t border-[#F9F9F6]/10 pt-4">
                  {o.items.map((l, idx) => (
                    <li key={idx} className="text-sm text-[#F9F9F6]/70">
                      <span className="flex justify-between gap-4">
                        <span>{l.quantity}× {l.name}</span>
                        <span className="text-[#F9F9F6]/50">R{l.line_total.toLocaleString()}</span>
                      </span>
                      {l.selections && l.selections.length > 0 && (
                        <span className="block mt-1 space-y-0.5">
                          {l.selections.map((s, si) => (
                            <span key={si} className="block text-[11px] text-[#D4AF37]/80">{s}</span>
                          ))}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
                {o.notes && <p className="text-xs text-[#F9F9F6]/50 mt-3 italic">Notes: {o.notes}</p>}
                {o.inspo && (
                  <a href={`${API}/files/${o.inspo}`} target="_blank" rel="noopener noreferrer" data-testid={`order-inspo-${o.ref}`}
                     className="inline-block mt-3 text-[10px] uppercase tracking-[0.15em] text-[#D4AF37] border border-[#D4AF37]/40 rounded-full px-3 py-1.5 hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-colors">
                    View cupcake inspo picture
                  </a>
                )}
              </article>
            ))}
          </div>
        )}

        {tab === "enquiries" && (
          <div className="space-y-4" data-testid="enquiries-list">
            {enquiries.length === 0 && <p className="text-[#F9F9F6]/40 text-sm">No enquiries yet.</p>}
            {enquiries.map((e) => (
              <article key={e.id} data-testid={`enquiry-row-${e.id.slice(0, 8)}`} className="border border-[#F9F9F6]/10 bg-[#1A1A1A] p-5 md:p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="font-serif-e text-xl text-[#F9F9F6]">{e.name}</p>
                    <p className="text-sm text-[#F9F9F6]/70 mt-1">
                      <a href={`mailto:${e.email}`} className="text-[#D4AF37] hover:underline">{e.email}</a>
                      {e.phone && <> · {e.phone}</>}
                    </p>
                    <p className="text-[10px] uppercase tracking-[0.15em] text-[#F9F9F6]/40 mt-1">{fmtDate(e.created_at)}</p>
                  </div>
                  <StatusPill value={e.status} options={ENQUIRY_STATUSES} onChange={(s) => setEnquiryStatus(e.id, s)} testId={`enquiry-status-${e.id.slice(0, 8)}`} />
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  <span className="text-[10px] uppercase tracking-[0.15em] border border-[#D4AF37]/40 text-[#D4AF37] rounded-full px-3 py-1">{e.event_type}</span>
                  {e.guests && <span className="text-[10px] uppercase tracking-[0.15em] border border-[#F9F9F6]/20 text-[#F9F9F6]/60 rounded-full px-3 py-1">{e.guests} guests</span>}
                  {e.event_date && <span className="text-[10px] uppercase tracking-[0.15em] border border-[#F9F9F6]/20 text-[#F9F9F6]/60 rounded-full px-3 py-1">{e.event_date}</span>}
                </div>
                <p className="text-sm text-[#F9F9F6]/70 mt-4 border-t border-[#F9F9F6]/10 pt-4 leading-relaxed">{e.message}</p>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
